import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from '@multipack/ui';
import App from './App';
import './index.css';

// Declare the build ID injected by vite config
declare global {
  interface Window {
    __BUILD_ID__?: string;
  }
}

const STORAGE_KEY = 'multipack-build-id';
const DYNAMIC_IMPORT_RELOAD_KEY = 'multipack:dynamic-import-reload';
const DYNAMIC_IMPORT_RELOAD_COOLDOWN_MS = 10000;

function shouldReloadForDynamicImportError(message?: string): boolean {
  return Boolean(message?.includes('Failed to fetch dynamically imported module'));
}

function triggerDynamicImportReload(source: string, message?: string): void {
  const lastReload = Number(sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) ?? 0);
  if (Date.now() - lastReload < DYNAMIC_IMPORT_RELOAD_COOLDOWN_MS) {
    return;
  }
  sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, String(Date.now()));
  console.warn(`[dev] Reloading after dynamic import failure (${source})`, message);
  window.location.reload();
}

// Check for build version changes and clear stale caches
async function handleVersionChange() {
  const currentBuildId = window.__BUILD_ID__;

  // In dev mode, always clear caches (no build ID in dev)
  if (import.meta.env.DEV || !currentBuildId) {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('[Dev] Unregistered service worker:', registration.scope);
      }
    }
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
        console.log('[Dev] Cleared cache:', cacheName);
      }
    }
    return;
  }

  // In production, check if build version changed
  const storedBuildId = localStorage.getItem(STORAGE_KEY);

  if (storedBuildId && storedBuildId !== currentBuildId) {
    console.log(`[Update] Build changed: ${storedBuildId} → ${currentBuildId}`);

    // Clear all caches from old version
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
        console.log('[Update] Cleared stale cache:', cacheName);
      }
    }

    // Force service worker to update
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.update();
        console.log('[Update] Triggered SW update:', registration.scope);
      }
    }
  }

  // Store current build ID
  localStorage.setItem(STORAGE_KEY, currentBuildId);
}

// Run version check (non-blocking)
handleVersionChange().catch(console.error);

window.addEventListener('error', (event) => {
  if (shouldReloadForDynamicImportError(event.message)) {
    triggerDynamicImportReload('error', event.message);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: string } | string | undefined;
  const message = typeof reason === 'string' ? reason : reason?.message;
  if (shouldReloadForDynamicImportError(message)) {
    triggerDynamicImportReload('unhandledrejection', message);
  }
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
